/**
 * FormDialogProvider.tsx
 *
 * Provides a context for the FormDialog component.
 * Ensures that the FormDialog component is only rendered once.
 *
 * @param children - The children to render.
 * @returns A React component that provides the FormDialog context.
 */
import { FormDialogState } from 'interfaces/formDialog';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import LazyFormDialog from 'components/LazyFormDialog';

type FormDialogContextValue = {
  openFormDialog: (state: FormDialogState) => void;
};

type StackedForm = FormDialogState & { id: number };

const FormDialogContext = createContext<FormDialogContextValue | undefined>(undefined);

export const FormDialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Get the current pathname
  const { pathname } = useLocation();
  const prevPathnameRef = useRef(pathname);

  // A form opened while another is showing, e.g. from one of its action buttons, stacks on top of it. Only the top
  // form renders, keyed by its id, so each one mounts fresh instead of inheriting the previous form's state.
  const [stack, setStack] = useState<StackedForm[]>([]);
  // The handlers read the stack from here: a save calls onSubmit and then onClose in the same tick, and the second
  // call has to see that the first already took the form off.
  const stackRef = useRef<StackedForm[]>([]);
  const nextIDRef = useRef(0);

  const updateStack = useCallback((next: StackedForm[]) => {
    stackRef.current = next;
    setStack(next);
  }, []);

  const isTop = (id: number) => stackRef.current[stackRef.current.length - 1]?.id === id;

  const closeAll = useCallback(
    (success?: boolean) => {
      const closing = stackRef.current;
      if (closing.length === 0) return;
      updateStack([]);
      [...closing].reverse().forEach((form, index) => form.onClose?.(index === 0 ? success : undefined));
    },
    [updateStack],
  );

  // Close dialog only when pathname changes (not when only query params change, and not when dialog is opened).
  useEffect(() => {
    if (prevPathnameRef.current === pathname) return;
    prevPathnameRef.current = pathname;
    closeAll();
  }, [pathname, closeAll]);

  const openFormDialog = useCallback(
    (state: FormDialogState) => {
      nextIDRef.current += 1;
      updateStack([...stackRef.current, { ...state, open: true, id: nextIDRef.current }]);
    },
    [updateStack],
  );

  // Takes the top form off, returning to the one it was opened from, if any.
  const pop = (id: number) => {
    if (!isTop(id)) return undefined;
    const form = stackRef.current[stackRef.current.length - 1];
    updateStack(stackRef.current.slice(0, -1));
    return form;
  };

  const handleSubmit = (id: number) => (success?: boolean) => {
    if (!isTop(id)) return;
    const [submitted, ...below] = [...stackRef.current].reverse();
    updateStack([]);
    submitted.onSubmit?.(success);
    submitted.onClose?.(success);
    below.forEach((form) => form.onClose?.());
  };

  const handleClose = (id: number) => (success?: boolean) => {
    if (isTop(id)) closeAll(success);
  };

  const handleBack = (id: number) => () => pop(id)?.onClose?.();

  const top = stack[stack.length - 1];
  let dialog: ReactNode = null;
  if (top) {
    const { id, ...state } = top;
    dialog = (
      <LazyFormDialog
        key={id}
        {...state}
        onBack={stack.length > 1 ? handleBack(id) : undefined}
        onClose={handleClose(id)}
        onSubmit={handleSubmit(id)}
      />
    );
  }

  return (
    <FormDialogContext.Provider value={{ openFormDialog }}>
      {children}
      {dialog}
    </FormDialogContext.Provider>
  );
};

export const useFormDialog = () => {
  const context = useContext(FormDialogContext);
  if (!context) {
    throw new Error('useFormDialog must be used within a FormDialogProvider');
  }
  return context;
};
